// src/components/ClientsListScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  HeartHandshake,
  MapPin,
  Menu,
  Plus,
  RefreshCw,
  Search,
  UserRound,
  UsersRound,
  Video,
  X,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/clientsList.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const NOTICE_TONE = {
  success: "success",
  warning: "warning",
  info: "info",
  neutral: "neutral",
};

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "ativos", label: "Cadastros ativos" },
  { key: "em-acompanhamento", label: "Em acompanhamento" },
  { key: "ciclo-encerrado", label: "Ciclo encerrado" },
  { key: "casais", label: "Casais" },
  { key: "online", label: "Online" },
  { key: "presencial", label: "Presencial" },
  { key: "sem-historico", label: "Sem histórico" },
];

function getNoticeTitle(type) {
  if (type === "success") return "Tudo certo";
  if (type === "warning") return "Atenção";
  if (type === "info") return "Aviso";
  return "Mensagem";
}

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function onlyNumbers(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatDateBR(dateLike) {
  try {
    if (!dateLike) return "Sem registro";
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "Sem registro";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  } catch {
    return "Sem registro";
  }
}

function formatDateTimeBR(dateLike) {
  try {
    if (!dateLike) return "";
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "CL";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

// Situação terapêutica é independente da situação cadastral.
// Não inferimos um ciclo aberto a partir de sessões antigas ou da data do último atendimento.
function getTherapyCycleInfo(client) {
  if (client?.dataFimTerapia) {
    return {
      key: "encerrado",
      label: "Ciclo encerrado",
      detail: `Encerrado em ${formatDateBR(client.dataFimTerapia)}`,
    };
  }

  if (client?.dataInicioTerapia) {
    return {
      key: "acompanhamento",
      label: "Em acompanhamento",
      detail: `Início em ${formatDateBR(client.dataInicioTerapia)}`,
    };
  }

  return {
    key: "nao-informado",
    label: "Ciclo não informado",
    detail: "Sem início de ciclo cadastrado",
  };
}

function getSessionInfo(client) {
  const tipo = String(client?.tipoUltimaSessao || "").trim();

  if (tipo === "Online") {
    return {
      label: "Online",
      tone: "blue",
      detail: "Último atendimento online",
      icon: Video,
    };
  }

  if (tipo === "Presencial") {
    return {
      label: "Presencial",
      tone: "green",
      detail: "Último atendimento presencial",
      icon: MapPin,
    };
  }

  return {
    label: "Sem histórico",
    tone: "neutral",
    detail: "Ainda sem sessão registrada",
    icon: Clock3,
  };
}

function NoticeBanner({ notice, onClose }) {
  if (!notice) return null;
  const tone = NOTICE_TONE[notice.type] || NOTICE_TONE.neutral;

  return (
    <section
      className={`clients-notice clients-notice--${tone}`}
      role="status"
      aria-live="polite"
    >
      <div className="clients-notice__icon" aria-hidden="true">
        {tone === "success" ? "✓" : tone === "warning" ? "!" : "i"}
      </div>

      <div className="clients-notice__body">
        <strong>{notice.title || getNoticeTitle(tone)}</strong>
        <span>{notice.message}</span>
      </div>

      {notice.actionLabel && typeof notice.onAction === "function" ? (
        <button
          className="clients-notice__action"
          type="button"
          onClick={notice.onAction}
        >
          {notice.actionLabel}
        </button>
      ) : null}

      <button
        className="clients-notice__close"
        type="button"
        onClick={onClose}
        aria-label="Fechar aviso"
      >
        <X size={16} />
      </button>
    </section>
  );
}

function ClientSkeleton() {
  return (
    <div className="clist-table clist-table--loading" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, index) => (
        <div className="clist-row clist-row--skeleton" key={index}>
          <span className="skeleton-block skeleton-avatar" />
          <span className="skeleton-block skeleton-main" />
          <span className="skeleton-block skeleton-contact" />
          <span className="skeleton-block skeleton-status" />
          <span className="skeleton-block skeleton-date" />
          <span className="skeleton-block skeleton-action" />
        </div>
      ))}
    </div>
  );
}

export default function ClientsListScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const noticeRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState(null);
  const [err, setErr] = useState("");
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const showNotice = (payload) => {
    setNotice({
      type: payload.type || "info",
      title: payload.title || getNoticeTitle(payload.type || "info"),
      message: payload.message,
      actionLabel: payload.actionLabel,
      onAction: payload.onAction,
      id: Date.now(),
    });

    window.requestAnimationFrame(() => {
      noticeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const filteredClients = useMemo(() => {
    const q = normalizeText(query);
    const qNumbers = onlyNumbers(query);

    return clients.filter((client) => {
      const sessionInfo = getSessionInfo(client);
      const cycleInfo = getTherapyCycleInfo(client);

      const filterMatches =
        activeFilter === "todos" ||
        (activeFilter === "ativos" && client?.ativo !== false) ||
        (activeFilter === "em-acompanhamento" && cycleInfo.key === "acompanhamento") ||
        (activeFilter === "ciclo-encerrado" && cycleInfo.key === "encerrado") ||
        (activeFilter === "casais" && client?.atendimentoCasal === true) ||
        (activeFilter === "online" && sessionInfo.label === "Online") ||
        (activeFilter === "presencial" && sessionInfo.label === "Presencial") ||
        (activeFilter === "sem-historico" && !client?.ultimaSessao);

      if (!filterMatches) return false;
      if (!q && !qNumbers) return true;

      const textSearch = normalizeText(
        [
          client?.nome,
          client?.email,
          client?.telefone,
          client?.cpf,
          client?.cidade,
          client?.estado,
          client?.conjuge?.nome,
        ]
          .filter(Boolean)
          .join(" ")
      );

      const numericSearch = onlyNumbers(
        [
          client?.telefone,
          client?.cpf,
          client?.cpfPagador,
          client?.cpfResponsavel,
          client?.conjuge?.cpf,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return (
        (q && textSearch.includes(q)) ||
        (qNumbers && numericSearch.includes(qNumbers))
      );
    });
  }, [activeFilter, clients, query]);

  const stats = useMemo(() => {
    const total = clients.length;
    const online = clients.filter(
      (client) => client?.tipoUltimaSessao === "Online"
    ).length;
    const presencial = clients.filter(
      (client) => client?.tipoUltimaSessao === "Presencial"
    ).length;
    const semHistorico = clients.filter((client) => !client?.ultimaSessao).length;
    const casais = clients.filter((client) => client?.atendimentoCasal === true).length;

    return { total, online, presencial, semHistorico, casais };
  }, [clients]);

  const loadClients = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);

    setErr("");

    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      const message = "Sessão inválida. Faça login novamente.";
      setErr(message);
      showNotice({
        type: "warning",
        title: "Sessão expirada",
        message,
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
      });
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      const url = `${API_BASE}/psy/client?id=${encodeURIComponent(userId)}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401 || response.status === 403) {
        const message =
          "Sua sessão expirou. Faça login novamente para acessar clientes.";
        setErr(message);
        showNotice({
          type: "warning",
          title: "Sessão expirada",
          message,
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      const newToken = response.headers.get("x-new-token");
      if (newToken) saveToken(newToken);

      if (response.status === 404) {
        setClients([]);
        setErr("");
        setLastUpdatedAt(new Date());
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const message = data?.message || "Falha ao carregar clientes.";
        setClients([]);
        setErr(message);
        showNotice({
          type: "warning",
          title: "Não foi possível carregar clientes",
          message,
          actionLabel: "Tentar novamente",
          onAction: () => loadClients({ silent: true }),
        });
        return;
      }

      const data = await response.json();
      setClients(Array.isArray(data) ? data : []);
      setLastUpdatedAt(new Date());

      if (silent) {
        showNotice({
          type: "success",
          title: "Lista atualizada",
          message: "A base de clientes foi atualizada com sucesso.",
        });
      }
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      const message = "Falha de comunicação com a API.";
      setClients([]);
      setErr(message);
      showNotice({
        type: "warning",
        title: "Falha de comunicação",
        message:
          "Não conseguimos conversar com a API agora. Verifique sua conexão e tente novamente.",
        actionLabel: "Tentar novamente",
        onAction: () => loadClients({ silent: true }),
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state?.refresh) {
      loadClients({ silent: true });
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const goNew = () => navigate("/clients/new");
  const openClient = (client) =>
    navigate("/clients/details", { state: { clientData: client } });
  const handleRefresh = () => loadClients({ silent: true });

  const clearSearch = () => {
    setQuery("");
    setActiveFilter("todos");
  };

  return (
    <div className="clients-page">
      <div className="clients-bg" aria-hidden="true">
        <span className="clients-bg__orb clients-bg__orb--one" />
        <span className="clients-bg__orb clients-bg__orb--two" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="clients-topbar">
        <button
          className="clients-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="clients-brand">
          <img src={Logo} alt="PsyAssist" className="clients-brand__logo" />
          <div className="clients-brand__text">
            <strong>Clientes</strong>
            <span>Base de pacientes • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="clients-topbar__actions">
          <button
            className="clients-btn clients-btn--ghost clients-btn--desktop"
            type="button"
            onClick={handleRefresh}
            disabled={loading || refreshing}
          >
            <RefreshCw size={16} className={refreshing ? "is-spinning" : ""} />
            {refreshing ? "Atualizando" : "Atualizar"}
          </button>

          <button
            className="clients-btn clients-btn--primary"
            type="button"
            onClick={goNew}
          >
            <Plus size={17} />
            Adicionar cliente
          </button>
        </div>
      </header>

      <main className="clients-main">
        <div ref={noticeRef}>
          <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
        </div>

        <section className="clients-hero">
          <div className="clients-hero__copy">
            <span className="clients-kicker">Gestão clínica</span>
            <h1>Clientes</h1>
            <p>
              Encontre rapidamente quem você atende, acompanhe o histórico e
              acesse o perfil clínico sem perder espaço com uma lista excessivamente aberta.
            </p>
          </div>

          <div className="clients-hero__status">
            <span className="clients-status-dot" />
            <div>
              <small>Status da base</small>
              <strong>{stats.total} cliente(s)</strong>
            </div>
          </div>
        </section>

        <section className="clients-stats" aria-label="Indicadores de clientes">
          <article className="clients-stat-card clients-stat-card--primary">
            <span className="clients-stat-card__icon"><UsersRound size={18} /></span>
            <div>
              <small>Total</small>
              <strong>{stats.total}</strong>
              <p>clientes na base</p>
            </div>
          </article>

          <article className="clients-stat-card">
            <span className="clients-stat-card__icon clients-stat-card__icon--blue"><Video size={18} /></span>
            <div>
              <small>Online</small>
              <strong>{stats.online}</strong>
              <p>último atendimento online</p>
            </div>
          </article>

          <article className="clients-stat-card">
            <span className="clients-stat-card__icon clients-stat-card__icon--green"><MapPin size={18} /></span>
            <div>
              <small>Presencial</small>
              <strong>{stats.presencial}</strong>
              <p>último atendimento presencial</p>
            </div>
          </article>

          <article className="clients-stat-card">
            <span className="clients-stat-card__icon clients-stat-card__icon--amber"><Clock3 size={18} /></span>
            <div>
              <small>Sem histórico</small>
              <strong>{stats.semHistorico}</strong>
              <p>aguardando primeira sessão</p>
            </div>
          </article>

          <article className="clients-stat-card">
            <span className="clients-stat-card__icon clients-stat-card__icon--rose"><HeartHandshake size={18} /></span>
            <div>
              <small>Casais</small>
              <strong>{stats.casais}</strong>
              <p>atendimentos de casal</p>
            </div>
          </article>
        </section>

        <section className="clist-panel">
          <div className="clist-panel__header">
            <div>
              <span className="clist-panel__eyebrow">Base de pacientes</span>
              <h2>Encontre e acesse um cliente</h2>
              <p>
                {loading
                  ? "Carregando clientes..."
                  : `${filteredClients.length} de ${clients.length} cliente(s) exibido(s)`}
              </p>
            </div>

            <div className="clist-panel__meta">
              {lastUpdatedAt ? (
                <span>Atualizado em {formatDateTimeBR(lastUpdatedAt)}</span>
              ) : null}
            </div>
          </div>

          <div className="clist-toolbar">
            <label className="clist-search">
              <Search size={17} className="clist-search__icon" />
              <input
                className="clist-search__input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por nome, e-mail, telefone, CPF ou parceiro..."
                aria-label="Buscar cliente"
              />
              {query ? (
                <button
                  className="clist-search__clear"
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Limpar busca"
                >
                  <X size={15} />
                </button>
              ) : null}
            </label>

            <button
              className="clients-btn clients-btn--ghost clients-btn--mobile-refresh"
              type="button"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              <RefreshCw size={16} className={refreshing ? "is-spinning" : ""} />
              Atualizar
            </button>
          </div>

          <div className="clist-filters" role="tablist" aria-label="Filtros de clientes">
            {FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`clist-filter ${
                  activeFilter === filter.key ? "clist-filter--active" : ""
                }`}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {loading ? (
            <ClientSkeleton />
          ) : err ? (
            <div className="clist-state clist-state--warning">
              <div className="clist-state__icon">!</div>
              <strong>Não foi possível carregar.</strong>
              <span>{err}</span>
              <button
                className="clients-btn clients-btn--primary"
                type="button"
                onClick={() => loadClients()}
              >
                Tentar novamente
              </button>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="clist-state">
              <div className="clist-state__icon"><UserRound size={23} /></div>
              <strong>
                {clients.length === 0
                  ? "Nenhum cliente cadastrado."
                  : "Nenhum cliente encontrado."}
              </strong>
              <span>
                {clients.length === 0
                  ? "Cadastre o primeiro paciente para começar a organizar sua base clínica."
                  : "Ajuste os filtros ou limpe a busca para visualizar outros clientes."}
              </span>
              <div className="clist-state__actions">
                {clients.length > 0 ? (
                  <button
                    className="clients-btn clients-btn--ghost clients-btn--light"
                    type="button"
                    onClick={clearSearch}
                  >
                    Limpar filtros
                  </button>
                ) : null}
                <button
                  className="clients-btn clients-btn--primary"
                  type="button"
                  onClick={goNew}
                >
                  <Plus size={16} />
                  Adicionar cliente
                </button>
              </div>
            </div>
          ) : (
            <div className="clist-table-wrap">
              <div className="clist-table-header" aria-hidden="true">
                <span>Paciente</span>
                <span>Contato</span>
                <span>Perfil</span>
                <span>Última sessão</span>
                <span>Ação</span>
              </div>

              <div className="clist-table">
                {filteredClients.map((client) => {
                  const sessionInfo = getSessionInfo(client);
                  const cycleInfo = getTherapyCycleInfo(client);
                  const SessionIcon = sessionInfo.icon;
                  const last = formatDateBR(client?.ultimaSessao);
                  const clientKey =
                    client?._id || client?.id || `${client?.email}-${client?.nome}`;
                  const isCouple = client?.atendimentoCasal === true;

                  return (
                    <button
                      key={clientKey}
                      className="clist-row"
                      type="button"
                      onClick={() => openClient(client)}
                      title={`Abrir ${client?.nome || "cliente"}`}
                    >
                      <div className="clist-patient">
                        <div className={`clist-avatar clist-avatar--${sessionInfo.tone}`}>
                          {getInitials(client?.nome)}
                        </div>

                        <div className="clist-patient__copy">
                          <div className="clist-name-row">
                            <strong className="clist-name">{client?.nome || "Sem nome"}</strong>
                            <span
                              className={`clist-cycle-badge clist-cycle-badge--${cycleInfo.key}`}
                              title={cycleInfo.detail}
                            >
                              {cycleInfo.label}
                            </span>
                            <span
                              className={`clist-active-badge ${client?.ativo === false ? "is-off" : ""}`}
                              title="Situação do cadastro (independente do acompanhamento terapêutico)"
                            >
                              {client?.ativo === false ? "Cadastro inativo" : "Cadastro ativo"}
                            </span>
                          </div>

                          <div className="clist-patient__meta">
                            {cycleInfo.key === "encerrado" ? (
                              <span className="clist-cycle-date">{cycleInfo.detail}</span>
                            ) : null}
                            {isCouple ? (
                              <span className="clist-couple-badge">
                                <HeartHandshake size={13} />
                                Casal
                              </span>
                            ) : (
                              <span>Individual</span>
                            )}

                            {isCouple && client?.conjuge?.nome ? (
                              <span className="clist-partner">com {client.conjuge.nome}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="clist-contact">
                        <strong>{client?.email || "E-mail não informado"}</strong>
                        <span>{client?.telefone || "Telefone não informado"}</span>
                      </div>

                      <div className="clist-profile-type">
                        <span className={`clist-session-badge clist-session-badge--${sessionInfo.tone}`}>
                          <SessionIcon size={13} />
                          {sessionInfo.label}
                        </span>
                        <small>{sessionInfo.detail}</small>
                      </div>

                      <div className="clist-last">
                        <span className="clist-last__icon"><CalendarDays size={15} /></span>
                        <div>
                          <strong>{last}</strong>
                          <small>{client?.ultimaSessao ? "último atendimento" : "sem atendimento"}</small>
                        </div>
                      </div>

                      <div className="clist-action">
                        <span>Ver perfil</span>
                        <ChevronRight size={17} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </main>

      <button
        className="clients-fab"
        type="button"
        onClick={goNew}
        aria-label="Adicionar cliente"
      >
        <Plus size={22} />
      </button>
    </div>
  );
}
